import { useMemo, useState } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, FormLabel, InputLabel, MenuItem, Radio, RadioGroup, Select, Stack, Typography } from "@mui/material";

import { authSessionAtom, authTokenAtom } from "atoms/authAtoms";
import { errorAtom } from "atoms/error";
import { useSnackbar } from 'utils/hooks/useSnackbar';
import { submitAllCopyExcel, submitAllCopyExcelHev, submitSubCopyExcel, submitUnitCopyExcel, submitUnitCopyExcelHev, submitSubCopyExcelHev, type FailedCopyItem } from "api/submitLtool";
import { copyToConfigAtom } from "atoms/Ltool/copyToConfigAtoms";
import { powertrainAtom } from "atoms/powertrainAtom";
import { gearIdAtom } from "atoms/gearAtom";
import { isConfigComplete } from "utils/utilsLtool";
import { PHENOMENON_GROUP_IDS, UNIT_IDS } from "utils/constsLtool";
import { CONDITION_GROUP_IDS as HEV_CONDITION_GROUP_IDS } from "utils/constsHev";
import { mappingAtom } from "atoms/mappingAtoms";
import { buildLabeledOptions } from "utils/mappingLabel";
import { naturalSortBy } from "utils/sort";
import { uploadExcelToS3 } from "../utils/uploadExcelToS3";
import {
    AllCopyFileSelector,
    UnitCopyFileSelector,
    SingleFileSelector,
    areAllFilesSelected,
    createEmptyAllCopyFiles,
    createEmptyPhenomenonFiles,
    UnitId,
    PhenomenonGroupId,
    AllCopyFilesMap,
    UnitCopyFilesMap,
} from "./ExcelImportSelectors";
import { toUserMessage } from "utils/apiErrorMessage";
import { FailedItemsList, buildCopyErrorMessage, classifySummary, buildResultMessage } from "components/FailedItemsList";

export const ExcelImport: React.FC = () => {
    const authToken = useAtomValue(authTokenAtom);
    const authSession = useAtomValue(authSessionAtom);
    const config = useAtomValue(copyToConfigAtom);
    const setError = useSetAtom(errorAtom);
    const { showSuccess, showError, showWarning } = useSnackbar();
    const mapping = useAtomValue(mappingAtom);
    const powertrain = useAtomValue(powertrainAtom);
    const gearId = useAtomValue(gearIdAtom);

    // HEV は aco を含まない condition_group（gn/vib）＋gear 単位 URL/キー（HEV対応仕様 §3, S3設計 §4.2）
    const isHev = powertrain === 'hev';
    const groupIds: readonly PhenomenonGroupId[] = isHev ? HEV_CONDITION_GROUP_IDS : PHENOMENON_GROUP_IDS;
    const gearArg = isHev ? (gearId ?? undefined) : undefined;

    // unit は定義順（マトリクスの行順と一致）、condition_group は表示ラベル基準で並べる（mapping 取得後に確定）
    const unitOptions = useMemo(
        () => buildLabeledOptions(mapping, 'unit_id', UNIT_IDS),
        [mapping],
    );
    const groupOptions = useMemo(
        () => naturalSortBy(buildLabeledOptions(mapping, 'condition_group_id', groupIds), (o) => o.label),
        [mapping, groupIds],
    );

    const [loading, setLoading] = useState(false);
    const [copyOpen, setCopyOpen] = useState(false);
    const [copyMode, setCopyMode] = useState<"all" | "unit" | "sub">("all");

    const [type, setType] = useState<"target" | "measurement">("target");
    const [unit_id, setUnitId] = useState<UnitId | "">("");
    const [conditionGroupId, setConditionGroupId] = useState<PhenomenonGroupId | "">("");
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [allCopyFiles, setAllCopyFiles] = useState<AllCopyFilesMap>(() => createEmptyAllCopyFiles());
    const [unitCopyFiles, setUnitCopyFiles] = useState<UnitCopyFilesMap>(() =>
        createEmptyPhenomenonFiles(),
    );
    const [failedItems, setFailedItems] = useState<FailedCopyItem[]>([]);
    const [copyMessage, setCopyMessage] = useState<string | null>(null);

    const resetSelections = () => {
        setUnitId("");
        setConditionGroupId("");
        setSelectedFile(null);
        setAllCopyFiles(createEmptyAllCopyFiles());
        setUnitCopyFiles(createEmptyPhenomenonFiles(groupIds));
        setFailedItems([]);
        setCopyMessage(null);
    };

    const openCopyDialog = (mode: "all" | "unit" | "sub") => {
        setCopyMode(mode);
        resetSelections();
        setCopyOpen(true);
    };

    const handleDialogClose = () => {
        setCopyOpen(false);
        resetSelections();
    };

    const handleConfirm = async () => {
        setError(null);
        setFailedItems([]);
        setCopyMessage(null);

        // HEV の gear 単位コピーは gear 必須（全データコピーは HEV では無効化済み）
        if (isHev && !gearId) {
            showWarning("HEV では Gear を選択してください（データ登録画面で選択）。");
            return;
        }

        if (copyMode === "all") {
            const uploadTargets: Array<{
                unit: UnitId;
                phenomenon: PhenomenonGroupId;
                file: File;
            }> = [];

            UNIT_IDS.forEach((unit) => {
                groupIds.forEach((phenomenon) => {
                    const file = allCopyFiles[unit][phenomenon];
                    if (!file) {
                        return;
                    }
                    uploadTargets.push({ unit, phenomenon, file });
                });
            });

            if (!areAllFilesSelected(allCopyFiles, groupIds)) {
                showWarning("全てのUnit IDと現象グループIDに対応するファイルを選択してください。");
                return;
            }

            setLoading(true);
            try {
                for (const { unit, phenomenon, file } of uploadTargets) {
                    await uploadExcelToS3(
                        authSession,
                        file,
                        config,
                        unit,
                        phenomenon,
                        type,
                        powertrain,
                        gearArg,
                    );
                }

                const response = isHev
                    ? await submitAllCopyExcelHev(authToken, config, gearId!, type)
                    : await submitAllCopyExcel(authToken, config, type);
                if (response.status >= 400) {
                    setCopyMessage(buildCopyErrorMessage(response));
                    setFailedItems(response.failed_items ?? []);
                    return;
                }
                const failed = response.failed_items ?? [];
                if (!response.summary) {
                    if (failed.length > 0) {
                        setCopyMessage("一部の登録に失敗しました。");
                        setFailedItems(failed);
                        return;
                    }
                    showSuccess(response.message || "コピー処理が完了しました。");
                } else if (classifySummary(response.summary) === 'all_success') {
                    showSuccess(buildResultMessage(response.summary));
                } else {
                    setCopyMessage(buildResultMessage(response.summary));
                    setFailedItems(failed);
                    return;
                }
                handleDialogClose();
            } catch (e) {
                const message = toUserMessage(e);
                setError(message);
                showError(message);
            } finally {
                setLoading(false);
            }
            return;
        }

        if (copyMode === "unit") {
            if (!unit_id) {
                showWarning("Unit IDを選択してください。");
                return;
            }

            const unitFiles = groupIds.map((phenomenon) => ({
                phenomenon,
                file: unitCopyFiles[phenomenon],
            }));

            if (unitFiles.some(({ file }) => !file)) {
                showWarning("選択したUnit IDの現象グループIDに対応する全てのファイルを選択してください。");
                return;
            }

            setLoading(true);
            try {
                for (const { phenomenon, file } of unitFiles) {
                    await uploadExcelToS3(
                        authSession,
                        file as File,
                        config,
                        unit_id,
                        phenomenon,
                        type,
                        powertrain,
                        gearArg,
                    );
                }

                const response = isHev
                    ? await submitUnitCopyExcelHev(authToken, config, gearId!, unit_id, type)
                    : await submitUnitCopyExcel(authToken, config, unit_id, type);

                if (response.status >= 400) {
                    setCopyMessage(buildCopyErrorMessage(response));
                    setFailedItems(response.failed_items ?? []);
                    return;
                }
                const failed = response.failed_items ?? [];
                if (!response.summary) {
                    if (failed.length > 0) {
                        setCopyMessage("一部の登録に失敗しました。");
                        setFailedItems(failed);
                        return;
                    }
                    showSuccess(response.message || "コピー処理が完了しました。");
                } else if (classifySummary(response.summary) === 'all_success') {
                    showSuccess(buildResultMessage(response.summary));
                } else {
                    setCopyMessage(buildResultMessage(response.summary));
                    setFailedItems(failed);
                    return;
                }
                handleDialogClose();
            } catch (e) {
                const message = toUserMessage(e);
                setError(message);
                showError(message);
            } finally {
                setLoading(false);
            }
            return;
        }

        if (!selectedFile) {
            showWarning("Excelファイルを選択してください。");
            return;
        }

        if (copyMode === "sub" && (!unit_id || !conditionGroupId)) {
            showWarning("Unit IDと現象グループIDを選択してください。");
            return;
        }

        setLoading(true);
        try {
            await uploadExcelToS3(
                authSession,
                selectedFile,
                config,
                unit_id,
                conditionGroupId,
                type,
                powertrain,
                gearArg,
            );

            if (copyMode === "sub") {
                const response = isHev
                    ? await submitSubCopyExcelHev(authToken, config, gearId!, unit_id, conditionGroupId, type)
                    : await submitSubCopyExcel(authToken, config, unit_id, conditionGroupId, type);
                if (response.status >= 400) {
                    setCopyMessage(buildCopyErrorMessage(response));
                    setFailedItems(response.failed_items ?? []);
                    return;
                }
                const failed = response.failed_items ?? [];
                if (!response.summary) {
                    if (failed.length > 0) {
                        setCopyMessage("一部の登録に失敗しました。");
                        setFailedItems(failed);
                        return;
                    }
                    showSuccess(response.message || "コピー処理が完了しました。");
                } else if (classifySummary(response.summary) === 'all_success') {
                    showSuccess(buildResultMessage(response.summary));
                } else {
                    setCopyMessage(buildResultMessage(response.summary));
                    setFailedItems(failed);
                    return;
                }
                handleDialogClose();
                return;
            }
        } catch (e) {
            const message = toUserMessage(e);
            setError(message);
            showError(message);
        } finally {
            setLoading(false);
        }
    };

    const filesReady =
        copyMode === "all"
            ? areAllFilesSelected(allCopyFiles, groupIds)
            : copyMode === "unit"
                ? groupIds.every((phenomenon) => Boolean(unitCopyFiles[phenomenon]))
                : Boolean(selectedFile);

    const confirmDisabled =
        loading ||
        !filesReady ||
        (copyMode === "unit" && !unit_id) ||
        (copyMode === "sub" && (!unit_id || !conditionGroupId)) ||
        (isHev && !gearId);

    return (
        <Box p={2}>
            <Typography>Excelファイルからデータをコピーします。</Typography>

            {/* 3つのコピー実行ボタン */}
            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                <Button
                    variant="contained"
                    sx={{ minWidth: 140 }}
                    disabled={!isConfigComplete(config) || loading}
                    onClick={() => openCopyDialog("all")}
                >
                    {loading ? "コピー中..." : "全データコピー"}
                </Button>
                <Button
                    variant="contained"
                    sx={{ minWidth: 140 }}
                    disabled={!isConfigComplete(config) || loading}
                    onClick={() => openCopyDialog("unit")}
                >
                    {loading ? "コピー中..." : "ユニット別データコピー"}
                </Button>
                <Button
                    variant="contained"
                    sx={{ minWidth: 140 }}
                    disabled={!isConfigComplete(config) || loading}
                    onClick={() => openCopyDialog("sub")}
                >
                    {loading ? "コピー中..." : "個別データコピー"}
                </Button>
            </Stack>

            {isHev && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    HEV では選択中の Gear（{gearId ?? '未選択'}）に対してコピーします。
                </Typography>
            )}

            {/* ★ 個別コピー用ダイアログ */}
            <Dialog open={copyOpen} onClose={handleDialogClose} fullWidth maxWidth="xs">
                <DialogTitle>
                    {copyMode === "all"
                        ? "全てのUnit IDと条件グループIDのExcelファイルを選択してください。"
                        : copyMode === "unit"
                            ? "Unit IDを選択してください。"
                            : "Unit IDと条件グループIDを選択してください"}
                </DialogTitle>
                <DialogContent>

                    <FormControl sx={{ mt: 1 }}>
                        <FormLabel id="type-radio-label">Type</FormLabel>
                        <RadioGroup
                            row
                            aria-labelledby="type-radio-label"
                            value={type}
                            onChange={(e) => setType(e.target.value as "target" | "measurement")}
                        >
                            <FormControlLabel value="target" control={<Radio />} label="目標値" />
                            <FormControlLabel value="measurement" control={<Radio />} label="設計値" />
                        </RadioGroup>
                    </FormControl>

                    {copyMode !== "all" && (
                        <FormControl fullWidth sx={{ mt: 1 }}>
                            <InputLabel id="drive-select-label">Unit ID</InputLabel>
                            <Select
                                labelId="drive-select-label"
                                label="Unit ID"
                                value={unit_id}
                                onChange={(e) => {
                                    const value = e.target.value as UnitId;
                                    setUnitId(value);
                                    if (copyMode === "unit") {
                                        setUnitCopyFiles(createEmptyPhenomenonFiles());
                                    }
                                }}
                            >
                                {unitOptions.map(({ value, label }) => (
                                    <MenuItem key={value} value={value}>{label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    {copyMode === "sub" && (
                        <FormControl fullWidth sx={{ mt: 1 }}>
                            <InputLabel id="phenomenon-group-label">条件グループID</InputLabel>
                            <Select
                                labelId="phenomenon-group-label"
                                value={conditionGroupId}
                                onChange={(e) => setConditionGroupId(e.target.value as PhenomenonGroupId)}
                            >
                                {groupOptions.map(({ value, label }) => (
                                    <MenuItem key={value} value={value}>
                                        {label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    {copyMode === "all" && (
                        <AllCopyFileSelector
                            files={allCopyFiles}
                            groupIds={groupIds}
                            onChange={(unit, phenomenon, file) =>
                                setAllCopyFiles((prev) => ({
                                    ...prev,
                                    [unit]: {
                                        ...prev[unit],
                                        [phenomenon]: file,
                                    },
                                }))
                            }
                        />
                    )}

                    {copyMode === "unit" && (
                        <UnitCopyFileSelector
                            files={unitCopyFiles}
                            unitSelected={Boolean(unit_id)}
                            groupIds={groupIds}
                            onChange={(phenomenon, file) =>
                                setUnitCopyFiles((prev) => ({
                                    ...prev,
                                    [phenomenon]: file,
                                }))
                            }
                        />
                    )}

                    {copyMode === "sub" && (
                        <SingleFileSelector
                            file={selectedFile}
                            onChange={(file) => setSelectedFile(file)}
                        />
                    )}

                    {copyMessage && (
                        <Box sx={{ mt: 2 }}>
                            <Typography color="error" variant="body2">
                                {copyMessage}
                            </Typography>
                            <FailedItemsList items={failedItems} />
                        </Box>
                    )}

                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogClose}>キャンセル</Button>
                    <Button
                        variant="contained"
                        onClick={handleConfirm}
                        disabled={confirmDisabled}
                        startIcon={loading ? <CircularProgress size={18} /> : undefined}
                    >
                        {loading ? "コピー中..." : "確定"}
                    </Button>
                </DialogActions>
            </Dialog>

        </Box>
    );
};
